import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useShakaPlayer } from '../../../../hooks/useShakaPlayer';
import { useCourseProgress } from '../../../../hooks/useCourseProgress';
import { courseService } from '../../../../services';
import { formatDuration } from '../../../../constants/mockDashboard';
import type {
  Course,
  Lesson,
  LessonAttachment,
  LessonProgress,
  Progress,
} from '../../../../types/course';
import './CoursePlayer.css';

function formatFileSize(kb: number): string {
  return kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

interface LessonListItemProps {
  lesson: Lesson;
  isActive: boolean;
  isCompleted: boolean;
  progressPercent: number;
  onClick: (lesson: Lesson) => void;
}

const LessonListItem: React.FC<LessonListItemProps> = ({
  lesson,
  isActive,
  isCompleted,
  progressPercent,
  onClick,
}) => (
  <button
    className={[
      'cp-lesson-item',
      isActive ? 'cp-lesson-item--active' : '',
      isCompleted ? 'cp-lesson-item--completed' : '',
    ]
      .filter(Boolean)
      .join(' ')}
    onClick={() => onClick(lesson)}
    aria-current={isActive ? 'true' : undefined}
  >
    <span className="cp-lesson-item__icon" aria-hidden="true">
      {isCompleted ? (
        <i className="bi bi-check-circle-fill" />
      ) : isActive ? (
        <i
          className={`bi bi-${lesson.type === 'audio' ? 'music-note' : 'play-circle-fill'}`}
        />
      ) : (
        <i
          className={`bi bi-${lesson.type === 'audio' ? 'music-note' : 'circle'}`}
        />
      )}
    </span>

    <span className="cp-lesson-item__body">
      <span className="cp-lesson-item__order">{lesson.order}.</span>
      <span className="cp-lesson-item__title">{lesson.title}</span>
      <span className="cp-lesson-item__duration">
        {lesson.type === 'audio' && (
          <i
            className="bi bi-headphones"
            style={{ marginRight: '0.25rem' }}
            aria-hidden="true"
          />
        )}
        {formatDuration(lesson.durationSeconds)}
      </span>
    </span>

    {!isCompleted && progressPercent > 0 && (
      <span className="cp-lesson-item__progress-bar" aria-hidden="true">
        <span
          className="cp-lesson-item__progress-fill"
          style={{ width: `${progressPercent}%` }}
        />
      </span>
    )}
  </button>
);

interface AttachmentListProps {
  attachments: LessonAttachment[];
  authToken: string;
  heading?: string;
}

const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  authToken,
  heading = 'Allegati lezione',
}) => {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const downloadFile = async (att: LessonAttachment) => {
    if (downloading) return;
    setDownloading(att.id);
    try {
      const res = await fetch(att.fileUrl, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${att.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Errore durante il download.';
      console.error('[Download]', message);
      setDownloading(null);
      setDownloadError(message ? message : 'Errore durante il download.');
      setTimeout(() => setDownloadError(null), 4000);
    } finally {
      setDownloading(null);
    }
  };

  if (attachments.length === 0) return null;

  return (
    <div className="cp-attachments">
      <h3 className="cp-attachments__title">
        <i className="bi bi-paperclip" aria-hidden="true" />
        {heading}
      </h3>
      {downloadError && (
        <div className="cp-download-error mb-2" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2" />
          {downloadError}
        </div>
      )}
      <ul className="cp-attachments__list" role="list">
        {attachments.map((att) => (
          <li key={att.id}>
            <button
              className="cp-attachment-item__btn"
              onClick={() => downloadFile(att)}
              disabled={downloading === att.id}
              aria-label={`Scarica ${att.title}`}
            >
              <i
                className={`bi ${downloading === att.id ? 'bi-hourglass-split' : 'bi-file-earmark-pdf-fill'}`}
                aria-hidden="true"
              />
              <span className="cp-attachment-item__name">{att.title}</span>
              {att.fileSizeKb && (
                <span className="cp-attachment-item__size">
                  {formatFileSize(att.fileSizeKb)}
                </span>
              )}
              <i
                className={`bi bi-download cp-attachment-item__dl${downloading === att.id ? ' cp-attachment-item__dl--loading' : ''}`}
                aria-hidden="true"
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CoursePlayer: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authToken = localStorage.getItem('bp_token') ?? '';

  const [course, setCourse] = useState<Course | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [loading, setLoading] = useState(true);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const currentLessonRef = useRef<Lesson | null>(null);
  const resumeAppliedRef = useRef<string | null>(null);

  useEffect(() => {
    currentLessonRef.current = currentLesson;
  }, [currentLesson]);

  useEffect(() => {
    if (!courseId) return;

    // Snapshot al momento in cui l'effect scatta (solo su cambio courseId),
    // evitando re-init del player ad ogni modifica dei query param.
    const lessonFromUrl = searchParams.get('lesson');

    const load = async () => {
      try {
        setLoading(true);
        setProgressLoaded(false);

        const [fetchedCourse, fetchedProgress] = await Promise.all([
          courseService.getCourse(courseId),
          courseService.getProgress(courseId),
        ]);

        setCourse(fetchedCourse);
        setProgress(fetchedProgress);
        setProgressLoaded(true);

        const lastLessonId = fetchedProgress?.lessonId;

        const initial =
          fetchedCourse.lessons.find((l) => l.id === lessonFromUrl) ??
          fetchedCourse.lessons.find((l) => l.id === lastLessonId) ??
          fetchedCourse.lessons[0] ??
          null;

        resumeAppliedRef.current = null;
        setCurrentLesson(initial);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const safeCourse = course ?? {
    id: '',
    title: '',
    lessons: [],
    attachments: [],
  };

  const initialProgress = useMemo(() => {
    if (!progress || !courseId) return undefined;

    const lessons =
      progress.lessons && Object.keys(progress.lessons).length > 0
        ? progress.lessons
        : (() => {
            const fallbackLessons: Record<string, LessonProgress> = {};

            for (const id of progress.completedLessonIds ?? []) {
              fallbackLessons[id] = {
                lessonId: id,
                completed: true,
                progressSeconds: 0,
                lastWatchedAt: progress.lastWatchedAt,
              };
            }

            if (progress.lessonId) {
              fallbackLessons[progress.lessonId] = {
                lessonId: progress.lessonId,
                completed: (progress.completedLessonIds ?? []).includes(
                  progress.lessonId
                ),
                progressSeconds: progress.secondsWatched ?? 0,
                lastWatchedAt: progress.lastWatchedAt,
              };
            }

            return fallbackLessons;
          })();

    return {
      courseId,
      lastLessonId: progress.lessonId ?? null,
      completionPercent: progress.completionPercent ?? 0,
      startedAt: progress.lastWatchedAt,
      lastAccessedAt: progress.lastWatchedAt,
      lessons,
    };
  }, [progress, courseId]);

  const {
    updateTime,
    markLessonCompleted,
    getLessonProgress,
    getCompletionPercent,
    getResumeTime,
    overallPercent,
  } = useCourseProgress({
    course: safeCourse,
    userId: user?.id ?? '',
    initialProgress,
    onSave: courseId
      ? async (
          _courseId: string,
          lessonId: string,
          secondsWatched: number,
          completed: boolean
        ) => {
          await courseService.saveProgress(
            courseId,
            lessonId,
            secondsWatched,
            completed
          );
        }
      : undefined,
  });

  const source = useMemo(() => {
    if (!progressLoaded) return null;
    if (!currentLesson?.streamUrl) return null;
    // Le lezioni audio sono file mp3/m4a diretti, non manifest HLS/DASH:
    // vanno riprodotte nativamente dal <video>, mai passate a Shaka Player
    // (che fallirebbe provando a interpretarle come playlist HLS).
    return {
      url: currentLesson.streamUrl,
      format:
        currentLesson.type === 'audio'
          ? ('file' as const)
          : currentLesson.format,
    };
  }, [currentLesson, progressLoaded]);

  const handleTimeUpdate = useCallback(
    (t: number) => {
      if (currentLesson) {
        updateTime(currentLesson.id, t, currentLesson.durationSeconds);
      }
    },
    [currentLesson, updateTime]
  );

  const handleEnded = useCallback(async () => {
    if (!currentLesson || !courseId) return;

    markLessonCompleted(currentLesson.id);

    try {
      await courseService.saveProgress(
        courseId,
        currentLesson.id,
        currentLesson.durationSeconds,
        true
      );
    } catch (err) {
      console.error('[Progress] handleEnded save error', err);
    }
  }, [currentLesson, courseId, markLessonCompleted]);

  const {
    buffering,
    error: playerError,
    isReady,
  } = useShakaPlayer({
    videoRef,
    source,
    authToken,
    onTimeUpdate: handleTimeUpdate,
    onEnded: handleEnded,
  });

  const saveCurrentProgress = useCallback(async () => {
    const lesson = currentLessonRef.current;
    const video = videoRef.current;

    if (!courseId || !lesson || !video) return;
    if (video.currentTime <= 0) return;

    const seconds = Math.floor(video.currentTime);
    const existingLessonProgress = getLessonProgress(lesson.id);
    const completed = existingLessonProgress?.completed ?? false;

    try {
      await courseService.saveProgress(courseId, lesson.id, seconds, completed);
    } catch (err) {
      console.error('[Progress] saveCurrentProgress error', err);
    }
  }, [courseId, getLessonProgress]);

  useEffect(() => {
    if (!isReady || !currentLesson || !progressLoaded || !videoRef.current)
      return;
    if (resumeAppliedRef.current === currentLesson.id) return;

    const resumeAt = getResumeTime(currentLesson.id);

    if (resumeAt > 0) {
      const apply = () => {
        if (videoRef.current) {
          videoRef.current.currentTime = resumeAt;
        }
      };

      apply();
      setTimeout(apply, 100);
      setTimeout(apply, 300);
      setTimeout(apply, 700);
    }

    resumeAppliedRef.current = currentLesson.id;
  }, [isReady, currentLesson?.id, progressLoaded, getResumeTime]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void saveCurrentProgress();
      }
    };

    const handlePageHide = () => {
      void saveCurrentProgress();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [saveCurrentProgress]);

  useEffect(() => {
    return () => {
      void saveCurrentProgress();
    };
  }, [saveCurrentProgress]);

  const handleSelectLesson = useCallback(
    async (lesson: Lesson) => {
      await saveCurrentProgress();
      resumeAppliedRef.current = null;
      setCurrentLesson(lesson);
      setSidebarOpen(false);

      setTimeout(() => {
        document
          .getElementById('cp-video-wrapper')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    },
    [saveCurrentProgress]
  );

  const lessonIndex =
    course?.lessons.findIndex((l) => l.id === currentLesson?.id) ?? -1;
  const hasPrev = lessonIndex > 0;
  const hasNext = course ? lessonIndex < course.lessons.length - 1 : false;

  const goToLesson = useCallback(
    (delta: -1 | 1) => {
      if (!course) return;
      const next = course.lessons[lessonIndex + delta];
      if (next) {
        void handleSelectLesson(next);
      }
    },
    [course, lessonIndex, handleSelectLesson]
  );

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
      </div>
    );
  }

  if (notFound || !course) {
    return (
      <div className="cp-not-found">
        <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
        <p>Corso non trovato o accesso non autorizzato.</p>
        <button
          className="cp-not-found__btn"
          onClick={() => navigate('/dashboard/corsi')}
        >
          Torna ai miei corsi
        </button>
      </div>
    );
  }

  const isAudio = currentLesson?.type === 'audio';
  const hasPdfOnly = currentLesson && !currentLesson.streamUrl;

  return (
    <div className="cp-root">
      <header className="cp-topbar">
        <button
          className="cp-topbar__back"
          onClick={() => navigate('/dashboard/corsi')}
          aria-label="Torna ai miei corsi"
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
          <span className="cp-topbar__back-label">I miei corsi</span>
        </button>

        <div className="cp-topbar__course-info">
          <span className="cp-topbar__course-title">{course.title}</span>
          <span className="cp-topbar__overall-progress">
            <span
              className="cp-topbar__progress-bar-track"
              role="progressbar"
              aria-valuenow={overallPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Progresso: ${overallPercent}%`}
            >
              <span
                className="cp-topbar__progress-bar-fill"
                style={{ width: `${overallPercent}%` }}
              />
            </span>
            <span className="cp-topbar__progress-label">{overallPercent}%</span>
          </span>
        </div>

        <button
          className="cp-topbar__sidebar-toggle"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-expanded={sidebarOpen}
          aria-controls="cp-sidebar"
          aria-label="Lista lezioni"
        >
          <i className="bi bi-layout-text-sidebar-reverse" aria-hidden="true" />
        </button>
      </header>

      <div className="cp-body">
        <main className="cp-main">
          <div
            id="cp-video-wrapper"
            className={`cp-video-wrapper${isAudio ? ' cp-video-wrapper--audio' : ''}`}
          >
            {buffering && (
              <div
                className="cp-video-overlay cp-video-overlay--buffering"
                aria-live="polite"
                aria-label="Caricamento…"
              >
                <div className="cp-spinner" aria-hidden="true" />
              </div>
            )}

            {playerError && (
              <div
                className="cp-video-overlay cp-video-overlay--error"
                role="alert"
              >
                <i
                  className="bi bi-exclamation-triangle-fill"
                  aria-hidden="true"
                />
                <span>{playerError}</span>
                <button
                  className="cp-video-overlay__retry"
                  onClick={() => setCurrentLesson((l) => (l ? { ...l } : l))}
                >
                  Riprova
                </button>
              </div>
            )}

            {hasPdfOnly && (
              <div
                className="cp-video-overlay"
                style={{ background: 'var(--surface-secondary)' }}
              >
                <i
                  className="bi bi-file-earmark-pdf-fill"
                  style={{ fontSize: '3rem' }}
                  aria-hidden="true"
                />
                <p style={{ marginTop: '1rem' }}>
                  Questa lezione contiene solo materiale PDF
                </p>
              </div>
            )}

            {isAudio && !hasPdfOnly && (
              <div className="cp-audio-cover" aria-hidden="true">
                <i className="bi bi-headphones" />
                <span>{currentLesson?.title}</span>
              </div>
            )}

            {!hasPdfOnly && source && (
              <video
                ref={videoRef}
                className={`cp-video${isAudio ? ' cp-video--audio' : ''}`}
                controls
                playsInline
                poster={!isAudio ? course.imageUrl : undefined}
                aria-label={currentLesson?.title ?? 'Lezione'}
              />
            )}
          </div>

          <div className="cp-lesson-nav">
            <button
              className="cp-lesson-nav__btn"
              onClick={() => void goToLesson(-1)}
              disabled={!hasPrev}
              aria-label="Lezione precedente"
            >
              <i className="bi bi-skip-backward-fill" aria-hidden="true" />
              <span>Precedente</span>
            </button>

            <div className="cp-lesson-nav__current">
              <span className="cp-lesson-nav__index">
                {lessonIndex + 1} / {course.lessons.length}
              </span>
              <span className="cp-lesson-nav__title">
                {currentLesson?.title}
              </span>
            </div>

            <button
              className="cp-lesson-nav__btn cp-lesson-nav__btn--next"
              onClick={() => void goToLesson(1)}
              disabled={!hasNext}
              aria-label="Lezione successiva"
            >
              <span>Successiva</span>
              <i className="bi bi-skip-forward-fill" aria-hidden="true" />
            </button>
          </div>

          <section className="cp-lesson-info" aria-label="Informazioni lezione">
            <h1 className="cp-lesson-info__title">{currentLesson?.title}</h1>
            {currentLesson?.description && (
              <p className="cp-lesson-info__description">
                {currentLesson.description}
              </p>
            )}
            <div className="cp-lesson-info__meta">
              <span>
                <i
                  className={`bi bi-${isAudio ? 'headphones' : 'camera-video'}`}
                  aria-hidden="true"
                />
                {isAudio ? 'Audio' : hasPdfOnly ? 'Solo PDF' : 'Video'}
              </span>
              {!hasPdfOnly && (
                <span>
                  <i className="bi bi-clock" aria-hidden="true" />
                  {formatDuration(currentLesson?.durationSeconds ?? 0)}
                </span>
              )}
              <span>
                <i className="bi bi-collection-play" aria-hidden="true" />
                Lezione {lessonIndex + 1} di {course.lessons.length}
              </span>
            </div>
          </section>

          {currentLesson?.attachments &&
            currentLesson.attachments.length > 0 && (
              <AttachmentList
                attachments={currentLesson.attachments}
                authToken={authToken}
                heading="Allegati lezione"
              />
            )}
        </main>

        <aside
          id="cp-sidebar"
          className={`cp-sidebar${sidebarOpen ? ' cp-sidebar--open' : ''}`}
          aria-label="Lista lezioni"
        >
          <div className="cp-sidebar__header">
            <div>
              <h2 className="cp-sidebar__heading">Lezioni</h2>
              <span className="cp-sidebar__count">
                {course.lessons.length} lezioni ·{' '}
                {formatDuration(
                  course.lessons.reduce((acc, l) => acc + l.durationSeconds, 0)
                )}
              </span>
            </div>
            <button
              className="cp-sidebar__close"
              onClick={() => setSidebarOpen(false)}
              aria-label="Chiudi lista lezioni"
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>

          <ul className="cp-sidebar__list" role="list">
            {course.lessons.map((lesson) => (
              <li key={lesson.id}>
                <LessonListItem
                  lesson={lesson}
                  isActive={lesson.id === currentLesson?.id}
                  isCompleted={getCompletionPercent(lesson.id) >= 100}
                  progressPercent={getCompletionPercent(lesson.id)}
                  onClick={handleSelectLesson}
                />
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {sidebarOpen && (
        <div
          className="cp-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default CoursePlayer;
